import React, { useState, useRef, useEffect } from 'react';
import { Box, Grid, Paper, Dialog, DialogActions, DialogContent, DialogTitle, Button, TextField, Typography } from '@mui/material';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import GroupTree from './GroupTree';
import DeviceGroupsDialog from './DeviceGroupsDialog';
import AvailablePatients from './AvailablePatients';
import CohortPatients from './CohortPatients';
// VICUService legacy compatibility removed; keep import commented for reference
// import VICUService from "../../services/VICUService";
import ClinicService from "../../services/ClinicService";
import PatientService from "../../services/PatientService";
import useFetch from "../../hooks/useFetch";
import { useCookies } from "react-cookie";
import { Device } from "../../interfaces/Device"
import { Patient } from "../../interfaces/Patient"
import { useTranslation } from "react-i18next";
import { useContext } from 'react';
import { UserContext } from "../../context/UserContext";


export interface TreeNode {
    group_id: string;
    group_name: string;
    primary_contact: string;
    children?: TreeNode[];
    level: 'clinic' | 'group';
    devices?: Device[]; // Keep for backward compatibility
    patients?: Patient[]; // Add patients for cohort assignments
}

//const initialData: TreeNode[] = initialDataJson as TreeNode[];

const MonitorGroupManager: React.FC = () => {
    const isAdmin = true;
    const user = useContext(UserContext);
    const my_organization_id = user?.user?.organization || 'default';
    //console.log("User context:", user);
    //console.log("Organization ID for patient fetch:", my_organization_id);
    const [groupData, setGroupData] = useState<TreeNode[]>([]);
    const [poolPatients, setPoolPatients] = useState<Patient[]>([]);
    const [groupDevices, setGroupDevices] = useState<{ [key: string]: Device[] }>({});
    const [groupPatients, setGroupPatients] = useState<{ [key: string]: Patient[] }>({});
    const [selectedGroup, setSelectedGroup] = useState<string>(""); // Default to no selection
    const selectedGroupRef = useRef<string>(""); // Default to no selection
    const [selectedGroupName, setSelectedGroupName] = useState<string>(''); // Default to no selection
    const [selectedContact, setSelectedContact] = useState<string>(''); // Default to no selection
    const [expanded, setExpanded] = useState<string[]>([]); // No default expansion
    const [openDialog, setOpenDialog] = useState<boolean>(false);
    const [editDialogOpen, setEditDialogOpen] = useState<boolean>(false);
    const [newGroupName, setNewGroupName] = useState<string>('');
    const [newContact, setNewContact] = useState<string>('');
    const [editGroupName, setEditGroupName] = useState<string>('');
    const [editContact, setEditContact] = useState<string>('');
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
    const [deviceGroupsDialogOpen, setDeviceGroupsDialogOpen] = useState<boolean>(false);
    const [groupIdNameMap, setGroupIdNameMap] = useState<{ [key: string]: string }>({});
    const [deleteConfirmDialogOpen, setDeleteConfirmDialogOpen] = useState<boolean>(false);
    const [dialogLevel, setDialogLevel] = useState<'clinic' | 'group'>('clinic');
    const [dialogParentId, setDialogParentId] = useState<string>('');
    const [enableDragAndDrop, setEnableDragAndDrop] = useState<boolean>(true);
    const [cookies] = useCookies(["access_token"]);
    //const theme = useTheme();
    const { t } = useTranslation();
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [isSaveDisabled, setIsSaveDisabled] = useState<boolean>(true); // Controls the Save button
    const [selectedGroupLevel, setSelectedGroupLevel] = useState<'clinic' | 'group'>('clinic');


    /*
     * Fetch the cohort list from clinics with their cohorts
     */
    const {
        data: initialMonitorGroupList,
        //loading: monitorGroupLoading,
        //error: monitorGroupError,
        //refresh: refreshMonitorGroups,
    } = useFetch({
        url: ClinicService.getAllClinics(undefined, true), // Include cohorts
        access_token: cookies.access_token,
    });

    /*
     * Fetch cohorts with patient associations for all clinics
     * Since clinic_id is required, we'll fetch after we get the clinic list
     */
    const [cohortsWithAssociations, setCohortsWithAssociations] = useState<any>(null);

    // Fetch cohorts with patient associations for all clinics
    useEffect(() => {
        if (initialMonitorGroupList?.results && cookies.access_token) {
            console.log("Fetching cohorts with patient associations for all clinics...");
            
            const fetchAllCohorts = async () => {
                try {
                    const allCohorts: any[] = [];
                    
                    // Fetch cohorts for each clinic
                    for (const clinic of initialMonitorGroupList.results) {
                        const clinicId = String(clinic.clinic_id);
                        console.log(`Fetching cohorts for clinic ${clinic.name} (ID: ${clinicId})...`);
                        
                        const cohortsUrl = ClinicService.getAllCohorts(clinicId, undefined, true);
                        console.log(`Cohorts URL: ${cohortsUrl}`);
                        
                        const response = await fetch(cohortsUrl, {
                            headers: {
                                'Authorization': `Bearer ${cookies.access_token}`,
                                'Content-Type': 'application/json'
                            }
                        });
                        
                        if (response.ok) {
                            const cohortsData = await response.json();
                            console.log(`Got cohorts for clinic ${clinic.name}:`, cohortsData);
                            
                            if (cohortsData.results) {
                                allCohorts.push(...cohortsData.results);
                            }
                        } else {
                            console.error(`Failed to fetch cohorts for clinic ${clinic.name}:`, response.status);
                        }
                    }
                    
                    console.log("All cohorts with associations:", allCohorts);
                    setCohortsWithAssociations({ results: allCohorts });
                    
                } catch (error) {
                    console.error("Error fetching cohorts with associations:", error);
                }
            };
            
            fetchAllCohorts();
        }
    }, [initialMonitorGroupList, cookies.access_token]);

    // Fetch patients data for RPM/CCM
    const {
        data: allPatients,
        loading: patientsLoading,
        error: patientsError,
        //refresh: refreshPatients,
    } = useFetch({
        url: PatientService.getPatients(my_organization_id),
        access_token: cookies.access_token,
    });

    // Log patient fetch status
    useEffect(() => {
        console.log("Patient fetch status:", { 
            loading: patientsLoading, 
            error: patientsError, 
            hasData: !!allPatients,
            url: PatientService.getPatients(my_organization_id)
        });
    }, [patientsLoading, patientsError, allPatients, my_organization_id]);

    // Log raw patient data from backend
    useEffect(() => {
        if (allPatients && Array.isArray(allPatients)) {
            console.log("Raw patient data from PatientService.getPatients:", allPatients);
            console.log("Patient count:", allPatients.length);
            if (allPatients.length > 0) {
                console.log("Sample patient object (first patient):", allPatients[0]);
                console.log("Patient object keys:", Object.keys(allPatients[0]));
            }
        }
    }, [allPatients]);

    const [monitorGroupRegister, setMonitorGroupRegister] = useState<{ [key: string]: Device[] }>({});

    const constructMonitorGroupRegistry = (nodes: TreeNode[]): { [key: string]: Device[] } => {
        const registry: { [key: string]: Device[] } = {};
    
        const traverseNodes = (nodeList: TreeNode[]) => {
            nodeList.forEach(node => {
                registry[node.group_id] = node.devices || [];
                if (node.children && node.children.length > 0) {
                    traverseNodes(node.children);
                }
            });
        };
    
        traverseNodes(nodes);
        return registry;
    };


    
    // Helper function to get pool patients (patients not assigned to any cohort)
    // Note: This is now calculated in the main useEffect using patient_associations data
    // Legacy helper removed; pool patients are calculated from cohort associations

    // Load initial data once
    useEffect(() => {
        if (initialMonitorGroupList) {
            console.log("initialMonitorGroupList (from ClinicService):", initialMonitorGroupList);

            // Transform clinic/cohort data to the expected VICU structure for backward compatibility
            const transformedGroupList: TreeNode[] = [];
            
            if (initialMonitorGroupList.results) {
                console.log("Detailed cohort data structure:");
                initialMonitorGroupList.results.forEach((clinic: any, clinicIndex: number) => {
                    console.log(`Clinic ${clinicIndex}:`, {
                        name: clinic.name,
                        clinic_id: clinic.clinic_id,
                        cohorts: clinic.cohorts?.map((c: any) => ({
                            name: c.name,
                            cohort_id: c.cohort_id,
                            patient_count: c.patient_count,
                            has_patient_associations: !!c.patient_associations,
                            associations_count: c.patient_associations?.length || 0
                        }))
                    });
                });
                    
                initialMonitorGroupList.results.forEach((clinic: any) => {
                    // Debug: Log clinic object to see its structure
                    console.log("Clinic object:", clinic);
                    
                    // Add the clinic as a top-level node with safe ID handling
                    console.log("Clinic ID details:", { 
                        clinic_id: clinic.clinic_id, 
                        id: clinic.id,
                        idType: typeof clinic.clinic_id, 
                        hasClinicId: !!clinic.clinic_id,
                        name: clinic.name 
                    });
                    
                    // Use clinic_id field as per API documentation
                    const clinicId = clinic.clinic_id || clinic.id;
                    if (!clinicId) {
                        console.error("Clinic missing valid ID:", clinic);
                    }
                    
                    const clinicNode: TreeNode = {
                        group_id: clinicId ? `clinic_${clinicId}` : (clinic.name || 'unknown'),
                        group_name: clinic.name || 'Unnamed Clinic',
                        level: 'clinic' as const,
                        primary_contact: clinic.description || '',
                        devices: [], // Clinics don't have devices directly
                        children: []
                    };

                    // Add cohorts as children if they exist
                    if (clinic.cohorts && Array.isArray(clinic.cohorts)) {
                        clinicNode.children = clinic.cohorts.map((cohort: any) => {
                            console.log("Cohort object:", cohort); // Debug cohort structure
                            console.log("Cohort ID details:", { 
                                cohort_id: cohort.cohort_id,
                                id: cohort.id, 
                                idType: typeof cohort.cohort_id, 
                                hasCohortId: !!cohort.cohort_id,
                                name: cohort.name 
                            });
                            
                            // Use cohort_id field as per API documentation
                            const cohortId = cohort.cohort_id || cohort.id;
                            if (!cohortId) {
                                console.error("Cohort missing valid ID:", cohort);
                            }
                            
                            return {
                                group_id: cohortId ? `cohort_${cohortId}` : (cohort.name || 'unknown'),
                                group_name: cohort.name || 'Unnamed Cohort',
                                level: 'group' as const,
                                primary_contact: cohort.description || '',
                                devices: [], // Will be populated later with patients
                                children: undefined
                            };
                        });
                    }

                    transformedGroupList.push(clinicNode);
                });
            }

            console.log("Transformed group list:", transformedGroupList);
            setGroupData(transformedGroupList);

            // to keep track of which devices are assigned to each of the cohorts.
            const register = constructMonitorGroupRegistry(transformedGroupList);
            setMonitorGroupRegister(register)

            //console.log("Cohort list:", transformedGroupList);
            //console.log("Cohort registry:", register);

            // Find the first clinic
            const firstClinic = transformedGroupList.find((group:any) => group.level === 'clinic');
            if (firstClinic) {
                handleNodeSelect(null, firstClinic.group_id);
                setExpanded([firstClinic.group_id]);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialMonitorGroupList]); // handleNodeSelect is stable and defined after this effect

    // Load patient data and set up patient cohort registry
    useEffect(() => {
        if (allPatients && Array.isArray(allPatients) && cohortsWithAssociations?.results) {
            console.log("allPatients:", allPatients);
            console.log("Cohorts with associations data:", cohortsWithAssociations);

            // Build a map of patient associations from cohort data
            const patientCohortMap: { [patientId: string]: number } = {};
            
            cohortsWithAssociations.results.forEach((cohort: any) => {
                console.log("Processing cohort for associations:", {
                    name: cohort.name,
                    cohort_id: cohort.cohort_id,
                    patient_count: cohort.patient_count,
                    has_patient_associations: !!cohort.patient_associations,
                    associations_count: cohort.patient_associations?.length || 0,
                    patient_associations: cohort.patient_associations
                });
                
                if (cohort.patient_associations && Array.isArray(cohort.patient_associations)) {
                    cohort.patient_associations.forEach((assoc: any) => {
                        if (assoc.is_active) {
                            patientCohortMap[assoc.patient_id] = cohort.cohort_id;
                            console.log(`✓ Patient ${assoc.patient_name} (${assoc.patient_id}) -> Cohort ${cohort.name} (${cohort.cohort_id})`);
                        }
                    });
                } else {
                    console.log(`⚠️  No patient_associations found for cohort ${cohort.name}`);
                }
            });

            console.log("Patient-Cohort mapping:", patientCohortMap);

            // Group patients by their cohort assignment (with cohort_ prefix for consistency)
            const patientsByCohort: { [key: string]: Patient[] } = {};
            const assignedPatientIds = new Set<string>();

            allPatients.forEach((patient: any) => {
                const cohortId = patientCohortMap[patient.patient_id];
                if (cohortId) {
                    const cohortKey = `cohort_${cohortId}`;
                    if (!patientsByCohort[cohortKey]) {
                        patientsByCohort[cohortKey] = [];
                    }
                    patientsByCohort[cohortKey].push(patient);
                    assignedPatientIds.add(patient.patient_id);
                    console.log(`Assigned patient ${patient.first_name} ${patient.last_name} to cohort ${cohortKey}`);
                }
            });

            // Get unassigned patients (pool patients)
            const poolPatients = allPatients.filter((patient: any) => !assignedPatientIds.has(patient.patient_id));

            setPoolPatients(poolPatients);
            setGroupPatients(patientsByCohort);

            console.log("Pool patients (unassigned):", poolPatients.length, poolPatients);
            console.log("Patients by cohort:", patientsByCohort);
        } else {
            // No patient data available - set empty states
            setPoolPatients([]);
            setGroupPatients({});
            console.log("No patient data or cohort data available");
        }
    }, [allPatients, cohortsWithAssociations]);

    useEffect(() => {
        selectedGroupRef.current = selectedGroup;
    }, [selectedGroup]);

    useEffect(() => {
        if (groupData === null) {
            return;
        }
        //console.log("Process groupData:", groupData);
        const generateGroupIdNameMap = (nodes: TreeNode[], map: { [key: string]: string } = {}): { [key: string]: string } => {
            nodes.forEach(node => {
                map[node.group_id] = node.group_name;
                if (node.children) {
                    generateGroupIdNameMap(node.children, map);
                }
            });
            return map;
        };
        const map = generateGroupIdNameMap(groupData);
        setGroupIdNameMap(map);
    }, [groupData]);



    useEffect(() => {
        if (groupData.length > 0) {
            // Find the first clinic
            const firstClinic = groupData.find(group => group.level === 'clinic');
            if (firstClinic) {
                handleNodeSelect(null, firstClinic.group_id);
                setExpanded([firstClinic.group_id]);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groupData]);

    const handleOpenDialog = (level: 'clinic' | 'group', parentId: string) => {
        //console.log("handleOpenDialog parent:", parentId);
        setNewGroupName('');
        setNewContact('')
        setDialogLevel(level);
        setDialogParentId(parentId);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setNewGroupName('');
        setNewContact('')
    };

    const handleAddGroupOrRegion = () => {
        console.log("handleAddGroupOrRegion started:", {
            newGroupName,
            dialogLevel,
            dialogParentId,
            selectedGroup
        });
        if (!newGroupName.trim()) return;


        if (newGroupName.trim().toLowerCase() === "syspool") {
            setErrorMessage(t("syspool_reserved_name")); // Notify if the name is "syspool"
            return;
        }
    
        setErrorMessage(""); // Clear error message on valid input
    
        // Create the clinic or cohort using the appropriate service
        if (dialogLevel === 'clinic') {
            // Create a new clinic
            const clinicData = {
                name: newGroupName,
                description: newContact || '',
                organization: my_organization_id || 'default',
                is_active: true
            };
            //console.log("Save Clinic:", clinicData)
            ClinicService.createClinic(clinicData, cookies.access_token)
                .then((res: any) => {
                    //console.log("Return data:", res)
                    const clinicId = res.data.clinic_id || res.data.id; // Use clinic_id from API response
                    const newContact = res.data.description;
                    const newGroup: TreeNode = { group_id: `clinic_${clinicId}`, group_name: newGroupName, level: dialogLevel, primary_contact: newContact };
                    
                    const addGroupToNode = (nodes: TreeNode[]): TreeNode[] => {
                        if (dialogLevel === 'clinic') {
                            return [...nodes, newGroup];
                        }

                        return nodes.map(node => {
                            if (node.group_id === dialogParentId && dialogLevel === 'group') {
                                return {
                                    ...node,
                                    children: node.children ? [...node.children, newGroup] : [newGroup],
                                };
                            } else if (node.children) {
                                return { ...node, children: addGroupToNode(node.children) };
                            }
                            return node;
                        });
                    };

                    setGroupData(addGroupToNode(groupData));
                    setExpanded([...expanded, newGroup.group_id]);

                }).catch((err: any) => {
                    console.log(err);
                });
        } else {
            // Create a new cohort within the selected clinic
            // Extract numeric clinic ID from prefixed dialogParentId (e.g., "clinic_4" -> 4)
            console.log("dialogParentId received:", dialogParentId, typeof dialogParentId);
            
            let clinicId: number;
            if (typeof dialogParentId === 'string' && dialogParentId.startsWith('clinic_')) {
                const numericPart = dialogParentId.replace('clinic_', '');
                clinicId = parseInt(numericPart);
                console.log("Extracted numeric part:", numericPart, "Parsed as integer:", clinicId);
            } else {
                clinicId = parseInt(dialogParentId);
                console.log("Direct parsing of dialogParentId:", clinicId);
            }
            
            // Validate that we have a valid clinic ID
            if (isNaN(clinicId) || clinicId <= 0) {
                console.error("Invalid clinic ID:", clinicId, "from dialogParentId:", dialogParentId);
                alert("Error: Invalid clinic selected. Please select a valid clinic.");
                return;
            }
            
            const cohortData = {
                name: newGroupName,
                description: newContact || '',
                clinic_id: clinicId, // Send as integer
                is_active: true
            };
            console.log("Save Cohort - Final data:", cohortData);
            console.log("About to call ClinicService.createCohort with:", JSON.stringify(cohortData));
            ClinicService.createCohort(cohortData, cookies.access_token)
                .then((res: any) => {
                    //console.log("Return data:", res)
                    const cohortId = res.data.cohort_id || res.data.id; // Use cohort_id from API response
                    const newContact = res.data.description;
                    const newGroup: TreeNode = { group_id: `cohort_${cohortId}`, group_name: newGroupName, level: dialogLevel, primary_contact: newContact };
                    
                    const addGroupToNode = (nodes: TreeNode[]): TreeNode[] => {
                        return nodes.map(node => {
                            if (node.group_id === dialogParentId) {
                                return {
                                    ...node,
                                    children: node.children ? [...node.children, newGroup] : [newGroup],
                                };
                            } else if (node.children) {
                                return { ...node, children: addGroupToNode(node.children) };
                            }
                            return node;
                        });
                    };

                    setGroupData(addGroupToNode(groupData));
                    setExpanded([...expanded, newGroup.group_id]);

                }).catch((err: any) => {
                    console.log(err);
                });
        }

        handleCloseDialog();
    };

    const handleDeleteGroupOrRegion = () => {
        if (!selectedGroup) return;
        //console.log("Delete:", selectedGroup, "Level:", selectedGroupLevel)

        // Extract the numeric ID from the prefixed group_id
        let numericId: string;
        if (selectedGroup.startsWith('clinic_')) {
            numericId = selectedGroup.replace('clinic_', '');
        } else if (selectedGroup.startsWith('cohort_')) {
            numericId = selectedGroup.replace('cohort_', '');
        } else {
            console.error("Invalid group ID format:", selectedGroup);
            return;
        }

        // Use the appropriate delete service based on whether it's a clinic or cohort
        const deletePromise = selectedGroupLevel === 'clinic' 
            ? ClinicService.deleteClinic(numericId, cookies.access_token)
            : ClinicService.deleteCohort(numericId, cookies.access_token);

        deletePromise
            .then((res: any) => {
                //console.log("delete result:", res);
                const deleteGroupFromNode = (nodes: TreeNode[]): TreeNode[] => {
                    return nodes.filter(node => node.group_id !== selectedGroup).map(node => {
                        if (node.children) {
                            return { ...node, children: deleteGroupFromNode(node.children) };
                        }
                        return node;
                    });
                };

                setGroupData(deleteGroupFromNode(groupData));
                setSelectedGroup(""); // Clear selection after deletion
            })
            .catch((err: any) => {
                console.log(err);
            });

        setSelectedGroupName(''); // Clear selected group name
        setSelectedContact('')
    };

    const handleNodeToggle = (event: React.SyntheticEvent, nodeIds: string[]) => {
        setExpanded(nodeIds);
    };


    const handleNodeSelect = (event: React.SyntheticEvent | null, nodeId: string) => {
        setSelectedGroup(nodeId);
    
        console.log("Selected group:", nodeId, "Current selectedGroup:", selectedGroup);
        // if the groupId exists in the gropuData array where the item.group_id matches groupId, then do nothing and just return
        if (groupData.some(item => item.group_id === nodeId)) {
            //console.log("Group exists in groupData:", nodeId);
            setEnableDragAndDrop (false)
        } else {
            setEnableDragAndDrop(true);
        }
        
        if (monitorGroupRegister[nodeId]) {
            setGroupDevices(prevGroupDevices => ({
                ...prevGroupDevices,
                [nodeId]: monitorGroupRegister[nodeId]
            }));
        }
    
        const findGroup = (nodes: TreeNode[]): TreeNode | null => {
            for (const node of nodes) {
                if (node.group_id === nodeId) {
                    return node;
                } else if (node.children) {
                    const foundGroup = findGroup(node.children);
                    if (foundGroup) return foundGroup;
                }
            }
            return null;
        };
    
        const group = findGroup(groupData);
        if (group) {
            console.log("Found group:", group);
            setSelectedGroupName(group.group_name);
            setSelectedContact(group.primary_contact);
            setSelectedGroupLevel(group.level); // Update the level state
            console.log("Updated selectedGroupName to:", group.group_name, "level:", group.level);
        } else {
            console.log("Group not found for nodeId:", nodeId);
        }
    
        if (!expanded.includes(nodeId)) {
            setExpanded([...expanded, nodeId]);
        }
    };
    



    const handleCloseDeviceGroupsDialog = () => {
        setDeviceGroupsDialogOpen(false);
        setSelectedDevice(null);
    };

    // Patient handlers for cohort management
    const handleDropToCohort = (patient: Patient) => {
        const selectedGroupId = selectedGroupRef.current;
        if (!selectedGroupId) return;

        // Extract the numeric cohort ID from the prefixed group_id (e.g., "cohort_1" -> "1")
        if (!selectedGroupId.startsWith('cohort_')) {
            console.error("Cannot assign patient to non-cohort selection:", selectedGroupId);
            return;
        }
        const cohortId = selectedGroupId.replace('cohort_', '');

        // Use the new ClinicService for patient association
        const associationData = {
            cohort_id: parseInt(cohortId),
            patient_ids: [patient.patient_id],
            notes: ""
        };

        console.log("Attempting to associate patient to cohort:", {
            selectedGroupId,
            cohortId,
            patient: patient.patient_id,
            associationData,
            hasToken: !!cookies.access_token,
            tokenLength: cookies.access_token ? cookies.access_token.length : 0,
            tokenPreview: cookies.access_token ? cookies.access_token.substring(0, 20) + "..." : "no token",
            endpoint: "POST /api/v1/cohort/patient-associations/bulk/",
            userContext: user,
            organizationId: my_organization_id
        });

        ClinicService.bulkAssociatePatients(associationData, cookies.access_token).then((res: any) => {
                console.log("Attach patient to cohort result:", res);

                // Check if this is actually an error response
                if (res.status >= 400) {
                    console.error("API Error - Patient association failed:", {
                        status: res.status,
                        statusText: res.statusText,
                        data: res.data,
                        url: res.config?.url
                    });
                    return; // Exit early, don't update state
                }

                // Update groupPatients state
                setGroupPatients(prevGroupPatients => {
                    const updatedGroupPatients = { ...prevGroupPatients };
                    if (!updatedGroupPatients[selectedGroupId]) {
                        updatedGroupPatients[selectedGroupId] = [];
                    }
                    if (!updatedGroupPatients[selectedGroupId].some(p => p.patient_id === patient.patient_id)) {
                        updatedGroupPatients[selectedGroupId].push(patient);
                    }
                    return updatedGroupPatients;
                });
            
                // Patient registry is handled by groupPatients state

                // Remove from pool patients
                setPoolPatients(prevPoolPatients => 
                    prevPoolPatients.filter(p => p.patient_id !== patient.patient_id)
                );
            })
            .catch((err: any) => {
                console.error("Patient association error:", {
                    error: err,
                    message: err.message,
                    status: err.response?.status,
                    statusText: err.response?.statusText,
                    data: err.response?.data,
                    config: err.config
                });
            });
    };

    // New: handle dropping a patient into AvailablePatients (unassign from its cohort)
    const handleUnassignPatientDrop = (patient: Patient) => {
        // Find which cohort currently contains this patient
        const cohortGroupKey = Object.keys(groupPatients).find(groupKey => {
            const list = groupPatients[groupKey];
            return Array.isArray(list) && list.some(p => p.patient_id === patient.patient_id);
        });

        if (!cohortGroupKey) {
            console.log("Patient", patient.patient_id, "not found in any cohort. No unassign needed.");
            // Ensure patient is in pool list
            setPoolPatients(prev => {
                if (prev.some(p => p.patient_id === patient.patient_id)) return prev;
                return [...prev, patient];
            });
            return;
        }

        if (!cohortGroupKey.startsWith('cohort_')) {
            console.warn("Group key", cohortGroupKey, "does not represent a cohort; aborting unassign.");
            return;
        }
        const cohortId = cohortGroupKey.replace('cohort_', '');
        const removalData = {
            cohort_id: parseInt(cohortId),
            patient_ids: [patient.patient_id]
        };
        console.log("Unassigning patient", patient.patient_id, "from cohort", cohortId, removalData);
        ClinicService.bulkRemovePatients(removalData, cookies.access_token).then((res: any) => {
            if (res.status && res.status >= 400) {
                console.error("API error while unassigning patient", res);
                return;
            }

            // Update state: remove from cohort list
            setGroupPatients(prev => {
                const updated = { ...prev };
                if (updated[cohortGroupKey]) {
                    updated[cohortGroupKey] = updated[cohortGroupKey].filter(p => p.patient_id !== patient.patient_id);
                }
                return updated;
            });
            // Add to pool if not present
            setPoolPatients(prev => {
                if (prev.some(p => p.patient_id === patient.patient_id)) return prev;
                return [...prev, patient];
            });
        }).catch((err: any) => {
            console.error("Failed to unassign patient", patient.patient_id, "from cohort", cohortId, err);
        });
    };

    const handleRemoveFromCohort = (patient: Patient) => {
        const selectedGroupId = selectedGroupRef.current;
        if (!selectedGroupId) return;

        // Extract the numeric cohort ID from the prefixed group_id (e.g., "cohort_1" -> "1")
        if (!selectedGroupId.startsWith('cohort_')) {
            console.error("Cannot remove patient from non-cohort selection:", selectedGroupId);
            return;
        }
        const cohortId = selectedGroupId.replace('cohort_', '');
    
        // Use the new ClinicService for patient removal
        const removalData = {
            cohort_id: parseInt(cohortId),
            patient_ids: [patient.patient_id]
        };

        ClinicService.bulkRemovePatients(removalData, cookies.access_token).then((res: any) => {
                console.log("Detach patient from cohort result:", res);

                // Check if this is actually an error response
                if (res.status >= 400) {
                    console.error("API Error - Patient removal failed:", {
                        status: res.status,
                        statusText: res.statusText,
                        data: res.data,
                        url: res.config?.url
                    });
                    return; // Exit early, don't update state
                }

                // Update groupPatients state
                setGroupPatients(prevGroupPatients => {
                    const updatedGroupPatients = { ...prevGroupPatients };
                    if (updatedGroupPatients[selectedGroupId]) {
                        updatedGroupPatients[selectedGroupId] = updatedGroupPatients[selectedGroupId].filter(p => p.patient_id !== patient.patient_id);
                    }
                    return updatedGroupPatients;
                });

                // Patient registry is handled by groupPatients state

                // Add back to pool patients
                setPoolPatients(prevPoolPatients => {
                    if (!prevPoolPatients.some(p => p.patient_id === patient.patient_id)) {
                        return [...prevPoolPatients, patient];
                    }
                    return prevPoolPatients;
                });
            })
            .catch((err: any) => {
                console.log("error:", err);
            });
    };

    const getFilteredPatients = (): Patient[] => {
        if (!poolPatients) {
            return []
        }
        if (!selectedGroup) return poolPatients;
        
        // Only filter out patients if a cohort is selected (not a clinic)
        if (!selectedGroup.startsWith('cohort_')) {
            return poolPatients;
        }
        
        const cohortPatientIds = groupPatients[selectedGroup]?.map(patient => patient.patient_id) || [];
        return poolPatients.filter((patient: Patient) => !cohortPatientIds.includes(patient.patient_id));
    };

    const handlePatientClick = (patient: Patient) => {
        // Could open patient details dialog in future
        console.log("Patient clicked:", patient);
    };

    const getDeviceGroups = (device: Device): string[] => {
        return Object.keys(groupDevices)
            .filter(groupId => groupDevices[groupId].some(d => d.device_id === device.device_id))
            .map(groupId => groupIdNameMap[groupId]);
    };

    const handleGroupClickInDialog = (groupName: string) => {
        const groupId = Object.keys(groupIdNameMap).find(id => groupIdNameMap[id] === groupName);
        if (!groupId) return;
        setDeviceGroupsDialogOpen(false);
        handleNodeSelect(null, groupId); // Trigger state updates as if clicked in tree
    };

    const handleEditGroup = () => {
        console.log ("Edit group:", selectedGroup, selectedGroupName, selectedContact, dialogLevel)
        setEditGroupName(selectedGroupName);
        setEditContact (selectedContact)
        setEditDialogOpen(true);
    };

    const handleSaveEditGroup = async () => {
        if (!selectedGroup || !editGroupName.trim()) return;

        try {
            // Use the appropriate update service based on whether it's a clinic or cohort
            const updateData = {
                name: editGroupName.trim(),
                description: editContact.trim()
            };
            
            // Extract the numeric ID from the prefixed group_id
            let numericId: string;
            if (selectedGroup.startsWith('clinic_')) {
                numericId = selectedGroup.replace('clinic_', '');
            } else if (selectedGroup.startsWith('cohort_')) {
                numericId = selectedGroup.replace('cohort_', '');
            } else {
                console.error("Invalid group ID format:", selectedGroup);
                return;
            }

            const res = selectedGroupLevel === 'clinic'
                ? await ClinicService.patchClinic(numericId, updateData, cookies.access_token)
                : await ClinicService.patchCohort(numericId, updateData, cookies.access_token);
            //console.log("Return data:", res);

            // Update group data with the new name
            const updateGroupName = (nodes: TreeNode[]): TreeNode[] => {
                return nodes.map(node => {
                    if (node.group_id === selectedGroup) {
                        return { ...node, group_name: res.data.name, primary_contact: res.data.description }; // Use correct API field names
                    } else if (node.children) {
                        return { ...node, children: updateGroupName(node.children) };
                    }
                    return node;
                });
            };

            const updatedGroupData = updateGroupName(groupData);

            // Update state
            setGroupData(updatedGroupData);
            setGroupIdNameMap(prevMap => ({ ...prevMap, [selectedGroup]: res.data.name }));
            setSelectedGroupName(res.data.name);
            setSelectedContact(res.data.description)

            setEditDialogOpen(false);
        } catch (err) {
            console.log(err);
        }
    };


    const handleDeleteGroup = () => {
        setDeleteConfirmDialogOpen(true);
    };

    const confirmDeleteGroup = () => {
        handleDeleteGroupOrRegion();
        setEditDialogOpen(false);
        setDeleteConfirmDialogOpen(false);
    };

    const handleCloseEditDialog = () => {
        setEditDialogOpen(false);
    };

    const handleCloseDeleteConfirmDialog = () => {
        setDeleteConfirmDialogOpen(false);
    };

    const handleAddClinicClick = () => {
        handleOpenDialog('clinic', '');  // No parent for clinic
    };

    const handleAddGroupClick = (clinicId?: string) => {
        // If clinicId is provided, use it; otherwise use the currently selected group if it's a clinic
        let parentClinicId = clinicId;
        
        if (!parentClinicId && selectedGroup && selectedGroup.startsWith('clinic_')) {
            parentClinicId = selectedGroup;
        }
        
        console.log("handleAddGroupClick - clinicId:", clinicId, "selectedGroup:", selectedGroup, "using parentClinicId:", parentClinicId);
        
        if (!parentClinicId) {
            alert("Please select a clinic first before adding a cohort.");
            return;
        }
        
        handleOpenDialog('group', parentClinicId);  // Pass the ID of the selected clinic
    };
    //console.log("Tree data:", groupData)


    const handleGroupNameChange = (value: string) => {
        const trimmedValue = value.trim();
    
        if (trimmedValue.toLowerCase() === "syspool") {
            setErrorMessage(t("syspool_reserved_name")); // Set error if reserved name
            setIsSaveDisabled(true); // Disable the Save button
        } else if (trimmedValue === "") {
            setErrorMessage(t("name_cannot_be_empty")); // Handle empty input
            setIsSaveDisabled(true);
        } else {
            setErrorMessage(""); // Clear error on valid input
            setIsSaveDisabled(false);
        }
    
        setNewGroupName(value); // Update the state
    };
    
    return (
        <DndProvider backend={HTML5Backend}>
            <Box sx={{ flexGrow: 1, p: 2 }}>
                <Grid
                    container
                    justifyContent="center"
                >
                    <Grid item>
                    <Typography variant="h4" sx={{ mb: 2 }}>{t ("cohort_management") }</Typography>
                    </Grid>
                </Grid>
                <Grid container spacing={2}>
                    <Grid item xs={3}>
                        <Paper elevation={3} sx={{ padding: 0 }}>
                            <GroupTree
                                data={groupData}
                                expanded={expanded}
                                selectedGroup={selectedGroup}
                                handleNodeToggle={handleNodeToggle}
                                handleNodeSelect={handleNodeSelect}
                                handleAddClinic={handleAddClinicClick}
                                handleAddGroup={handleAddGroupClick}
                                isAdmin={isAdmin}
                            />

                        </Paper>
                        {isAdmin && (<Button sx={{mt:2}} variant="outlined"  onClick={handleAddClinicClick} color="primary">
                            { t("add_clinic") }
                        </Button>)}
                    </Grid>
                    <Grid item xs={4}>
                        <CohortPatients
                            patients={groupPatients[selectedGroup] || []}
                            selectedCohortName={selectedGroupName}
                            cohortContact={selectedContact}
                            allowDragAndDrop={enableDragAndDrop}
                            handleDropToCohort={handleDropToCohort}
                            handleRemoveFromCohort={handleRemoveFromCohort}
                            isAdmin={isAdmin}
                            handleEditCohort={handleEditGroup}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <AvailablePatients
                            patients={getFilteredPatients()}
                            searchTerm={searchTerm}
                            setSearchTerm={setSearchTerm}
                            handlePatientClick={handlePatientClick}
                            onPatientDropped={handleUnassignPatientDrop}
                            enableDrop={true}
                        />
                    </Grid>
                </Grid>



                <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
                    <DialogTitle>
                        { t("add_new_" + (dialogLevel === 'clinic' ? 'clinic' : 'group'))}
                    </DialogTitle>
                    <DialogContent>
                        <TextField
                            autoFocus
                            margin="dense"
                            label={t("name")}
                            fullWidth
                            value={newGroupName}
                            onChange={(e) => handleGroupNameChange(e.target.value)} // Validate on input
                            error={!!errorMessage} // Highlight the field on error
                            helperText={errorMessage} // Display the error message
                                            />
                        <Grid item>
                            <TextField
                                label={t("contact")}
                                id="contact"
                                value={newContact}
                                fullWidth
                                onChange={(e) => setNewContact(e.target.value)}
                                sx={{ mt: 2 }} // Adds top margin
                            />
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                            <Button onClick={handleCloseDialog} >
                                {t("cancel")}
                            </Button>
                            <Button onClick={handleAddGroupOrRegion} color="primary" disabled={isSaveDisabled}>
                                {t("save")}
                            </Button>
                        </Box>
                    </DialogActions>
                </Dialog>

                <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="md" fullWidth>
                    <DialogTitle>{t ("Edit " + (selectedGroupLevel === 'clinic' ? 'Clinic' : 'Group') + " Name")}</DialogTitle>
                    <DialogContent>
                        <TextField
                            autoFocus
                            margin="dense"
                            label={ t ((selectedGroupLevel === 'clinic' ? 'Clinic' : 'Group') + " Name")}
                            fullWidth
                            value={editGroupName}
                            onChange={(e) => setEditGroupName(e.target.value)}
                        />
                        <Grid item>
                            <TextField
                                label={ t ("contact") }
                                id="contact"
                                value={editContact}
                                fullWidth
                                onChange={(e) => setEditContact(e.target.value)}
                                sx={{ mt: 2 }} // Adds top margin
                            />
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                            <Button onClick={handleCloseEditDialog} color="primary">
                                {t("cancel")}
                            </Button>
                            <Button onClick={handleDeleteGroup} color="warning">
                                {t("delete")}
                            </Button>
                            <Button onClick={handleSaveEditGroup} color="primary">
                                {t("save")}
                            </Button>
                        </Box>
                    </DialogActions>
                </Dialog>

                <Dialog open={deleteConfirmDialogOpen} onClose={handleCloseDeleteConfirmDialog}>
                    <DialogTitle>{t("confirm_delete")}</DialogTitle>
                    <DialogContent>
                        <Typography>
                        { t ((selectedGroupLevel === 'clinic' ? 'confirm_delete_clinic' : 'confirm_delete_group'))}
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDeleteConfirmDialog} color="primary">
                            {t("cancel")}
                        </Button>
                        <Button variant="contained"  onClick={confirmDeleteGroup} color="warning">
                            {t("confirm")}
                        </Button>
                    </DialogActions>
                </Dialog>

                <DeviceGroupsDialog
                    open={deviceGroupsDialogOpen}
                    onClose={handleCloseDeviceGroupsDialog}
                    selectedDevice={selectedDevice}
                    getDeviceGroups={getDeviceGroups}
                    handleGroupClickInDialog={handleGroupClickInDialog}
                />

            </Box>
        </DndProvider>
    );
};

export default MonitorGroupManager;
